<script setup lang="ts">
import type { ComputedRef } from "vue"
import type { Album, Media } from "@/models"
import type { ISelectItem } from "@/types"
import { useMutation, useQueryClient } from "@tanstack/vue-query"
import { computed, ref } from "vue"
import { LIST_ALBUMS_QUERY_KEY, moveToAlbum, useListAlbums } from "@/api"
import { MediaTile } from "@/components"
import { BaseModal, Button, Card, Icon, Select } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { MediaMoveModalKey } from "@/symbols"

interface MoveMediaModalProps {
  media?: Media[]
}

const props = withDefaults(defineProps<MoveMediaModalProps>(), {
  media: () => [],
})

const emit = defineEmits<{
  (e: "success"): void
  (e: "close"): void
  (e: "error", error: unknown): void
}>()

const eventStore = useEventVaultStore()

const { t } = useTranslation()

const targetAlbumId = ref<number | undefined>()
const client = useQueryClient()
const { toggle, isActive } = useModal(MediaMoveModalKey)

const { data: albums, isLoading } = useListAlbums(eventStore.eventVaultId)

const { mutate: moveMedia, isPending: isMoveMediaLoading } = useMutation({
  mutationFn: () => moveToAlbum(eventStore.eventVaultId, props.media, targetAlbumId.value),
  onSuccess: async () => {
    await client.invalidateQueries({ queryKey: [LIST_ALBUMS_QUERY_KEY] })
    toggle()
    emit("success")
  },
  onError: err => emit("error", err),
})

const albumSelectOptions: ComputedRef<ISelectItem[]> = computed(() => {
  const albumOptions
    = albums.value?.map((album: Album) => ({
      label: album?.title ?? "Unknown Album",
      value: album.id,
    })) ?? []

  return [{ label: t("media_move_modal.main_gallery_label"), value: -1 }, ...albumOptions]
})

function onToggle() {
  if (isActive?.value) {
    emit("close")
  }

  toggle()
}
</script>

<template>
  <div>
    <BaseModal :name="MediaMoveModalKey" :allow-overflow="true">
      <Card>
        <template #header>
          <div class="gpx-move-media-header">
            <h1>{{ t("media_move_modal.heading") }} </h1>
            <Button variant="icon" @click="onToggle">
              <span class="sr-only">{{ t("media_move_modal.close_sr_label") }}</span>
              <Icon icon="Close" size="sm" />
            </Button>
          </div>
        </template>
        <div v-if="isLoading || isMoveMediaLoading">
          <div class="gpx-move-media-grid">
            <div class="gpx-skeleton gpx-move-media-tile_loading" />
            <div class="gpx-skeleton gpx-move-media-tile_loading" />
            <div class="gpx-skeleton gpx-move-media-tile_loading" />
            <div class="gpx-skeleton gpx-move-media-tile_loading" />
          </div>
          <div class="gpx-skeleton gpx-move-media-select-label_loading" />
          <div class="gpx-skeleton gpx-move-media-select_loading" />
          <div class="gpx-move-media-actions">
            <div class="gpx-skeleton gpx-move-media-action_loading" />
            <div class="gpx-skeleton gpx-move-media-action_loading" />
          </div>
        </div>
        <div v-else>
          <div class="gpx-move-media-grid">
            <MediaTile v-for="(item, index) in media.slice(0, 3)" :key="`media-${index}`" :media="item" />
            <div v-if="media.slice(3).length" class="gpx-move-media-tile rounded-lg">
              <div class="gpx-move-media-tile-details">
                <h5>{{ t("media_move_modal.plus_more", { count: media.slice(3).length }) }} </h5>
              </div>
            </div>
          </div>
          <div class="gpx-move-media-album">
            <Select
              v-model="targetAlbumId" name="target-album" :items="albumSelectOptions"
              label="Move to Album" placeholder="Select Album"
            />
          </div>
          <div class="gpx-move-media-actions">
            <Button variant="flat" @click="onToggle">
              {{ t('cancel') }}
            </Button>
            <Button variant="primary" @click="moveMedia">
              {{ t('confirm') }}
            </Button>
          </div>
        </div>
      </Card>
    </BaseModal>
  </div>
</template>

<style lang="postcss" scoped>
@reference "../../assets/styles.css";

.gpx-move-media-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.gpx-move-media-header h1 {
  @apply text-2xl;
}

.gpx-move-media-header button {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}

.gpx-move-media-grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr;
  margin-top: 1.5rem;
}

.gpx-move-media-tile {
  display: flex;
  gap: 1rem;
  padding: 0.75rem;

  @apply border border-gray-300;
}

.gpx-move-media-tile-details h5 {
  margin: 0 0 0.25rem;

  @apply font-montserrat text-gray-900 font-light;
}

.gpx-move-media-album {
  margin: 1rem 0;
}

.gpx-move-media-actions {
  display: flex;
  width: 100%;
  gap: 0.5rem;
  align-items: flex-end;
  justify-content: end;
}

.gpx-move-media-tile_loading {
  height: 6rem;
}

.gpx-move-media-select-label_loading {
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
  height: 1.25rem;
  width: 10rem;
}

.gpx-move-media-select_loading {
  width: 100%;
  height: 2.5rem;
  margin-bottom: 1rem;
}

.gpx-move-media-action_loading {
  width: 8rem;
  height: 2.5rem;
}

@media (width >=768px) {
  .gpx-move-media-grid {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
